// ManageSKUModal.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import oapiAxios from '../axiosInstance';
import { fetchOrderwiseToken } from '../orderwiseApi';
import { attachYearValues, matchesYearFilter } from '../utils/yearFilter';

const EMPTY_USAGE_FILTERS = {
  make: '',
  model: '',
  years: '',
  region: '',
  itemType: '',
  fitment: ''
};

const filterInputStyle = {
  width: '100%',
  padding: '6px 8px',
  border: '1px solid var(--modal-border-gray)',
  borderRadius: '6px',
  fontSize: '13px',
  boxSizing: 'border-box'
};

const oemTableHeaderStyle = {
  background: 'var(--modal-bg-gray)',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: '13px',
  padding: '8px 10px',
  borderBottom: '1px solid var(--modal-border-gray)'
};

const oemTableCellStyle = {
  padding: '8px 10px',
  borderBottom: '1px solid var(--modal-table-border-gray)',
  fontSize: '13px',
  background: 'var(--modal-bg-white-cell)'
};

function collapseUsageRows(rawRows = []) {
  const groups = new Map();

  for (const r of rawRows || []) {
    const modelLabel = `${r.ModelName ?? ''}${r.StreetName ? ' ' + r.StreetName : ''}`.trim();
    const rawSort = Number(r.SortOrder ?? r.ModelSortOrder ?? r.Model_SortOrder);
    const sortOrder = Number.isFinite(rawSort) ? rawSort : 999999;
    const key = [r.Make || '', modelLabel, r.Region || '', r.ItemType || '', r.FitmentLocation || ''].join('§');
    if (!groups.has(key)) {
      groups.set(key, { years: [], modelSort: sortOrder, additional: r.AdditionalFitmentInfo || '' });
    }
    const group = groups.get(key);
    if (!group.additional && r.AdditionalFitmentInfo) {
      group.additional = r.AdditionalFitmentInfo;
    }
    const yearNumber = Number(r.Year);
    if (!Number.isNaN(yearNumber)) {
      group.years.push(yearNumber);
    }
  }

  const toRanges = (ordered) => {
    if (!ordered.length) return '';
    const ranges = [];
    let start = ordered[0];
    let prev = ordered[0];
    for (let i = 1; i < ordered.length; i++) {
      const current = ordered[i];
      if (current === prev + 1) {
        prev = current;
      } else {
        ranges.push(start === prev ? `${start}` : `${start}-${prev}`);
        start = prev = current;
      }
    }
    if (start !== undefined) {
      ranges.push(start === prev ? `${start}` : `${start}-${prev}`);
    }
    return ranges.join(', ');
  };

  return [...groups.entries()].map(([key, group]) => {
    const [Make, Model, Region, ItemType, FitmentLocation] = key.split('§');
    const orderedYears = attachYearValues(group.years);
    return {
      Make,
      Model,
      Years: orderedYears.length ? toRanges(orderedYears) : '',
      yearValues: orderedYears,
      Region,
      ItemType,
      FitmentLocation,
      modelSort: group.modelSort,
      AdditionalFitmentInfo: group.additional || ''
    };
  }).sort((a, b) =>
    a.modelSort - b.modelSort ||
    (a.ItemType || '').localeCompare(b.ItemType || '') ||
    (a.FitmentLocation || '').localeCompare(b.FitmentLocation || '') ||
    (a.AdditionalFitmentInfo || '').localeCompare(b.AdditionalFitmentInfo || '') ||
    a.Model.localeCompare(b.Model)
  );
}

export default function ManageSKUModal({ open, onClose, initialSKUGroup, initialSKUGroupKey = null, initialSKU, isDark, onApply, canEdit = true }) {
  const [search, setSearch] = useState(initialSKUGroup || '');
  const [results, setResults] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [usage, setUsage] = useState([]);
  const [busy, setBusy] = useState(false);
  const [apiData, setApiData] = useState({});
  const [error, setError] = useState('');
  const readOnly = !canEdit;
  const ensureWritable = useCallback(() => {
    if (!readOnly) return true;
    setError('Viewer access: editing is disabled for your role.');
    return false;
  }, [readOnly]);

  // Group fields
  const [groupForm, setGroupForm] = useState({
    SKUGroupKey: null,
    SKUGroup: '',
    SKUGroupDescription: ''
  });

  // SKUs and selected SKU
  const [skus, setSkus] = useState([]);
  const [skuForm, setSkuForm] = useState({
    SKUKey: null,
    SKU: '',
    SKUDescription: '',
    Brand: '',
    Quality: '',
    LocationMade: '',
    SKUGroupDifferences: ''
  });

  // Filters
  const [fMake, setFMake] = useState('');
  const [fModel, setFModel] = useState('');
  const [fYears, setFYears] = useState('');
  const [fRegion, setFRegion] = useState('');
  const [fItemType, setFItemType] = useState('');
  const [fFitment, setFFitment] = useState('');

  // Infinite scroll
  const [visibleCount, setVisibleCount] = useState(100);
  const gridRef = useRef(null);

  // OEM usage modal
  const [oemUsageOpen, setOemUsageOpen] = useState(false);
  const [oemUsageBusy, setOemUsageBusy] = useState(false);
  const [oemUsageError, setOemUsageError] = useState('');
  const [oemList, setOemList] = useState([]);
  const [oemSearch, setOemSearch] = useState('');
  const [selectedOem, setSelectedOem] = useState(null);
  const [oemUsageRaw, setOemUsageRaw] = useState([]);
  const [oemFilters, setOemFilters] = useState({ ...EMPTY_USAGE_FILTERS });

  function resetOemUsageModal() {
    setOemUsageOpen(false);
    setOemUsageBusy(false);
    setOemUsageError('');
    setOemList([]);
    setOemSearch('');
    setSelectedOem(null);
    setOemUsageRaw([]);
    setOemFilters({ ...EMPTY_USAGE_FILTERS });
  }

  const fetchToken = async () => {
    try {
      const token = await fetchOrderwiseToken();
      return token;
    } catch (err) {
      console.error('Token fetch error:', err);
      setError(err?.response?.data?.error || 'Failed to get token');
      return null;
    }
  };

async function fetchVariantDetails(sku) {
  if (!sku.trim()) return null;
  try {
    const token = await fetchToken();
    if (!token) return null;
    const response = await oapiAxios.post('/fetch-variant-detail', [{ name: '@SKU', value: sku }], {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      timeout: 300_000,
    });
    //console.log('Fetched variant detail for SKU:', sku, response.data);
    return Array.isArray(response.data) && response.data.length > 0 ? response.data[0] : null;
  } catch (err) {
    console.error('Error fetching variant detail for SKU:', sku, err);
    setError('Failed to fetch variant details from Orderwise API.');
    return null;
  }
}

  // Reset when closing/opening
  useEffect(() => {
    if (!open) {
      setSearch(initialSKUGroup || '');
      setResults([]);
      setSelectedGroup(null);
      setGroupForm({ SKUGroupKey: null, SKUGroup: initialSKUGroup || '', SKUGroupDescription: '' });
      setSkus([]);
      setSkuForm({
        SKUKey: null,
        SKU: initialSKU || '',
        SKUDescription: '',
        Brand: '',
        Quality: '',
        LocationMade: '',
        SKUGroupDifferences: ''
      });
      setApiData({});
      setUsage([]);
      setError('');
      setBusy(false);
      setFMake(''); setFModel(''); setFYears(''); setFRegion(''); setFItemType(''); setFFitment('');
      setVisibleCount(100);
      resetOemUsageModal();
    }
  }, [open, initialSKUGroup, initialSKU]);

  // Preselect current group
  useEffect(() => {
    if (!open) return;
    (async () => {
      const key = Number(initialSKUGroupKey);
      if (Number.isFinite(key) && key > 0) {
        try {
          await loadGroup(key, { preselectSku: initialSKU });
          return;
        } catch { /* ignore */ }
      }

      if (initialSKUGroup && initialSKUGroup.trim()) {
        try {
          const { data } = await axios.get('/api/sku-groups/search', { params: { query: initialSKUGroup } });
          const hit = (data || []).find(r => (r.SKUGroup || '').trim() === initialSKUGroup.trim()) || (data || [])[0];
          if (hit) await loadGroup(hit.SKUGroupKey, { preselectSku: initialSKU });
        } catch { /* ignore */ }
      }
    })();
  }, [open, initialSKUGroup, initialSKUGroupKey, initialSKU]);

  // Fetch variant details when SKU changes for a new SKU
  useEffect(() => {
    if (skuForm.SKUKey !== null || !skuForm.SKU.trim()) return;
    (async () => {
      setBusy(true);
      const details = await fetchVariantDetails(skuForm.SKU);
      if (details) {
        setSkuForm(prev => ({
          ...prev,
          SKUDescription: details.vad_description || prev.SKUDescription,
          Brand: details.md_name || prev.Brand,
          LocationMade: details.cl_country || prev.LocationMade,
        }));
        setApiData(details);
      } // No else; form remains editable if no API data
      setBusy(false);
    })();
  }, [skuForm.SKU, skuForm.SKUKey]);

  const onGroupField = useCallback((name, value) => {
    if (readOnly) return;
    setGroupForm(prev => ({ ...prev, [name]: value }));
  }, [readOnly]);
  const onSkuField = useCallback((name, value) => {
    if (readOnly) return;
    setSkuForm(prev => ({ ...prev, [name]: value }));
  }, [readOnly]);

  const resultsRef = useRef(null);
  const [page, setPage] = useState(1);
  const pageSize = 100;
  const [hasMore, setHasMore] = useState(false);

async function loadMoreIfNeeded(e) {
  const el = e?.currentTarget || resultsRef.current;
  if (!el) return;
  const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 24;
  if (nearBottom && hasMore) {
    await doSearch(false); // Load next page without resetting
  }
}

  async function doSearch(reset = true) {
    setError('');
    const q = search.trim();
    const nextPage = reset ? 1 : page + 1;
    const { data } = await axios.get('/api/sku-groups/search', { params: { query: q, page: nextPage, pageSize } });
    setResults(prev => reset ? (data || []) : [...(prev || []), ...(data || [])]);
    setPage(nextPage);
    setHasMore(Array.isArray(data) && data.length === pageSize);
  }

  async function loadGroup(skuGroupKey, options = {}) {
    setError('');
    const [g, s, u] = await Promise.all([
      axios.get(`/api/sku-groups/${skuGroupKey}`),
      axios.get(`/api/sku-groups/${skuGroupKey}/skus`),
      axios.get(`/api/sku-groups/${skuGroupKey}/usage`)
    ]);
    setSelectedGroup(g.data || null);
    setGroupForm({
      SKUGroupKey: g.data?.SKUGroupKey ?? null,
      SKUGroup: g.data?.SKUGroup || '',
      SKUGroupDescription: g.data?.SKUGroupDescription || ''
    });
    const list = Array.isArray(s.data) ? s.data : [];
    setSkus(list);
    setUsage(Array.isArray(u.data) ? u.data : []);

    const wantedSku = options.preselectSku ? options.preselectSku.trim().toLowerCase() : null;
    let first = null;
    if (wantedSku) {
      first = list.find(item => (item.SKU || '').trim().toLowerCase() === wantedSku) || null;
    }
    if (!first) first = list[0];
    if (first) {
      await chooseSKU(first);
    } else {
      setSkuForm({
        SKUKey: null,
        SKU: '',
        SKUDescription: '',
        Brand: '',
        Quality: '',
        LocationMade: '',
        SKUGroupDifferences: ''
      });
      setApiData({});
    }
    setVisibleCount(100);
  }

  async function openOemUsageWindow() {
    if (!groupForm.SKUGroupKey) {
      setError('Select a SKU Group first.');
      return;
    }
    setOemUsageOpen(true);
    setOemUsageBusy(true);
    setOemUsageError('');
    setOemSearch('');
    setOemFilters({ ...EMPTY_USAGE_FILTERS });
    try {
      const { data } = await axios.get(`/api/sku-groups/${groupForm.SKUGroupKey}/oem-usage`);
      const list = Array.isArray(data) ? data : [];
      setOemList(list);
      if (list.length) {
        await selectOemForModal(list[0]);
      } else {
        setSelectedOem(null);
        setOemUsageRaw([]);
      }
    } catch (err) {
      setSelectedOem(null);
      setOemUsageRaw([]);
      setOemUsageError(err?.response?.data?.error || 'Failed to load OEM usage for this group.');
    } finally {
      setOemUsageBusy(false);
    }
  }

  async function selectOemForModal(oem) {
    if (!oem) {
      setSelectedOem(null);
      setOemUsageRaw([]);
      return;
    }
    setSelectedOem(oem);
    setOemUsageBusy(true);
    setOemUsageError('');
    try {
      const { data } = await axios.get(`/api/oems/${oem.OEMKey}/usage`);
      setOemUsageRaw(Array.isArray(data) ? data : []);
    } catch (err) {
      setOemUsageRaw([]);
      setOemUsageError(err?.response?.data?.error || 'Failed to load usage for this OEM.');
    } finally {
      setOemUsageBusy(false);
    }
  }

  function closeOemUsageModal() {
    resetOemUsageModal();
  }

  function clearOemUsageFilters() {
    setOemFilters({ ...EMPTY_USAGE_FILTERS });
  }

  function handleOemFilterChange(field, value) {
    setOemFilters(prev => ({ ...prev, [field]: value }));
  }

  function copyOemUsageRows(rows) {
    if (!rows.length) return;
    const head = 'Make\tModel\tYears\tRegion\tItemType\tFitmentLocation';
    const lines = rows.map(r => [r.Make, r.Model, r.Years, r.Region, r.ItemType, r.FitmentLocation].join('\t'));
    navigator.clipboard.writeText([head, ...lines].join('\n'));
  }

  function handleUseInKit() {
    if (!ensureWritable()) return;
    if (typeof onApply !== 'function') return;
    onApply({
      SKUGroupKey: groupForm.SKUGroupKey ?? null,
      SKUGroup: groupForm.SKUGroup || '',
      SKUGroupDescription: groupForm.SKUGroupDescription || '',
      SKUKey: skuForm.SKUKey ?? null,
      SKU: skuForm.SKU || '',
      SKUDescription: skuForm.SKUDescription || ''
    });
    if (typeof onClose === 'function') onClose();
  }

  async function saveGroup() {
    if (!ensureWritable()) return;
    try {
      setBusy(true); setError('');
      const body = { ...groupForm };
      if (body.SKUGroupKey) {
        await axios.patch(`/api/sku-groups/${body.SKUGroupKey}`, body);
        await loadGroup(body.SKUGroupKey);
      } else {
        const { data } = await axios.post('/api/sku-groups', body);
        if (data?.SKUGroupKey) await loadGroup(data.SKUGroupKey);
      }
    } catch (err) {
      setError(err?.response?.data?.error || 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  async function deleteGroup() {
    if (!ensureWritable()) return;
    if (!groupForm.SKUGroupKey) return;
    if (!window.confirm(`Delete SKU Group ${groupForm.SKUGroup}? This cannot be undone.`)) return;
    try {
      setBusy(true); setError('');
      await axios.delete(`/api/sku-groups/${groupForm.SKUGroupKey}`);
      setSelectedGroup(null);
      setGroupForm({ SKUGroupKey: null, SKUGroup: '', SKUGroupDescription: '' });
      setSkus([]);
      setSkuForm({ SKUKey: null, SKU: '', SKUDescription: '', Brand: '', Quality: '', LocationMade: '', SKUGroupDifferences: '' });
      setApiData({});
      setUsage([]);
      if (search.trim()) await doSearch();
    } catch (err) {
      setError(err?.response?.data?.error || 'Delete failed.');
    } finally {
      setBusy(false);
    }
  }

  async function chooseSKU(sku) {
    setSkuForm({
      SKUKey: sku.SKUKey ?? null,
      SKU: sku.SKU || '',
      SKUDescription: sku.SKUDescription || '',
      Brand: sku.Brand || '',
      Quality: sku.Quality || '',
      LocationMade: sku.LocationMade || '',
      SKUGroupDifferences: sku.SKUGroupDifferences || ''
    });
    setApiData({});
    setBusy(true);
    const details = await fetchVariantDetails(sku.SKU);
    if (details) {
      setSkuForm(prev => ({
        ...prev,
        SKUDescription: details.vad_description || prev.SKUDescription,
        Brand: details.md_name || prev.Brand,
        LocationMade: details.cl_country || prev.LocationMade,
      }));
      setApiData(details);
    }
    setBusy(false);
  }

  async function saveSKU() {
    if (!ensureWritable()) return;
    if (!groupForm.SKUGroupKey) { setError('Select a SKU Group first.'); return; }
    try {
      setBusy(true); setError('');
      const body = { ...skuForm };
      if (body.SKUKey) {
        await axios.patch(`/api/sku-groups/${groupForm.SKUGroupKey}/skus/${body.SKUKey}`, body);
      } else {
        const { data } = await axios.post(`/api/sku-groups/${groupForm.SKUGroupKey}/skus`, body);
        body.SKUKey = data?.SKUKey ?? body.SKUKey;
      }
      await loadGroup(groupForm.SKUGroupKey);
      const again = (skus || []).find(x => x.SKUKey === body.SKUKey);
      if (again) await chooseSKU(again);
    } catch (err) {
      setError(err?.response?.data?.error || 'SKU save failed.');
    } finally {
      setBusy(false);
    }
  }

  async function deleteSKU() {
    if (!ensureWritable()) return;
    if (!groupForm.SKUGroupKey || !skuForm.SKUKey) return;
    if (!window.confirm(`Remove SKU ${skuForm.SKU} from group ${groupForm.SKUGroup}?`)) return;
    try {
      setBusy(true); setError('');
      await axios.delete(`/api/sku-groups/${groupForm.SKUGroupKey}/skus/${skuForm.SKUKey}`);
      await loadGroup(groupForm.SKUGroupKey);
    } catch (err) {
      setError(err?.response?.data?.error || 'SKU delete failed.');
    } finally {
      setBusy(false);
    }
  }

  // Build grouped usage rows and sort
  const usageRows = useMemo(() => collapseUsageRows(usage), [usage]);

  // Filtered and visible
  const filteredRows = useMemo(() => {
    let res = [...usageRows];
    const lc = str => (str || '').toLowerCase();
    if (fMake.trim()) res = res.filter(r => lc(r.Make).includes(lc(fMake)));
    if (fModel.trim()) res = res.filter(r => lc(r.Model).includes(lc(fModel)));
  if (fYears.trim()) res = res.filter(r => matchesYearFilter(r.yearValues, r.Years, fYears));
    if (fRegion.trim()) res = res.filter(r => lc(r.Region).includes(lc(fRegion)));
    if (fItemType.trim()) res = res.filter(r => lc(r.ItemType).includes(lc(fItemType)));
    if (fFitment.trim()) res = res.filter(r => lc(r.FitmentLocation).includes(lc(fFitment)));
    return res;
  }, [usageRows, fMake, fModel, fYears, fRegion, fItemType, fFitment]);

  const visibleRows = filteredRows.slice(0, visibleCount);
  const filteredCount = filteredRows.length;
  const totalRows = usageRows.length;

  const filteredOemList = useMemo(() => {
    const raw = oemSearch.trim().toLowerCase();
    const list = oemList || [];
    if (!raw) return list;

    const knownTypes = new Set(list.map(o => (o?.OEMType || '').toLowerCase()).filter(Boolean));
    const tokens = raw.split('~').map(token => token.trim()).filter(Boolean);

    // Simple search with no chained filters: allow substring fallback unless it exactly matches a known type
    if (!tokens.length) {
      if (knownTypes.has(raw)) {
        return list.filter(o => (o?.OEMType || '').toLowerCase() === raw);
      }
      return list.filter(o => {
        const number = String(o?.OEMNumber || '').toLowerCase();
        const type = String(o?.OEMType || '').toLowerCase();
        return number.includes(raw) || type.includes(raw);
      });
    }

    const typeTokens = [];
    const numberTokens = [];
    tokens.forEach(token => {
      if (knownTypes.has(token)) typeTokens.push(token);
      else numberTokens.push(token);
    });

    return list.filter(o => {
      const typeValue = String(o?.OEMType || '').toLowerCase();
      const numberValue = String(o?.OEMNumber || '').toLowerCase();
      const matchesType = typeTokens.length ? typeTokens.some(token => typeValue === token) : true;
      const matchesNumbers = numberTokens.every(token => numberValue.includes(token));
      return matchesType && matchesNumbers;
    });
  }, [oemList, oemSearch]);

  const collapsedOemUsageRows = useMemo(() => collapseUsageRows(oemUsageRaw), [oemUsageRaw]);

  const visibleOemUsageRows = useMemo(() => {
    let rows = [...collapsedOemUsageRows];
    const lc = (str = '') => String(str || '').toLowerCase();
    if (oemFilters.make.trim()) rows = rows.filter(r => lc(r.Make).includes(lc(oemFilters.make)));
    if (oemFilters.model.trim()) rows = rows.filter(r => lc(r.Model).includes(lc(oemFilters.model)));
  if (oemFilters.years.trim()) rows = rows.filter(r => matchesYearFilter(r.yearValues, r.Years, oemFilters.years));
    if (oemFilters.region.trim()) rows = rows.filter(r => lc(r.Region).includes(lc(oemFilters.region)));
    if (oemFilters.itemType.trim()) rows = rows.filter(r => lc(r.ItemType).includes(lc(oemFilters.itemType)));
    if (oemFilters.fitment.trim()) rows = rows.filter(r => lc(r.FitmentLocation).includes(lc(oemFilters.fitment)));
    return rows;
  }, [collapsedOemUsageRows, oemFilters]);

  const oemUsageStats = {
    total: collapsedOemUsageRows.length,
    filtered: visibleOemUsageRows.length
  };

  async function onGridScroll(e) {
    const el = e?.currentTarget || gridRef.current;
    if (!el) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 24;
    if (nearBottom && visibleCount < filteredRows.length) {
      setVisibleCount(prev => Math.min(prev + 100, filteredRows.length));
    }
  }

  function clearFilters() {
    setFMake(''); setFModel(''); setFYears(''); setFRegion(''); setFItemType(''); setFFitment('');
  }

  function copyUsage() {
    const head = 'Make\tModel\tYears\tRegion\tItemType\tFitmentLocation';
    const lines = filteredRows.map(r => [r.Make, r.Model, r.Years, r.Region, r.ItemType, r.FitmentLocation].join('\t'));
    navigator.clipboard.writeText([head, ...lines].join('\n'));
  }

  if (!open) return null;

  return (
    <>
      {error && (
        <div
          className="pim-modal-alert pim-modal-alert--error"
          style={{
            position: 'fixed',
            top: '2rem',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 5002
          }}
        >
          {String(error)}
        </div>
      )}
      <div
        className="pim-modal-surface"
        style={{
          position: 'fixed',
          left: '50%',
          top: '5vh',
          transform: 'translateX(-50%)',
          width: '90vw',
          maxWidth: '1800px',
          maxHeight: '90vh',
          zIndex: 5001,
          display: 'grid',
          gridTemplateRows: 'auto minmax(0,1fr)'
        }}
      >
        <div className="pim-modal-surface__header">
          <div style={{ fontWeight: 600, fontSize: '16px' }}>
            Manage SKU Group <span style={{ color: 'var(--modal-text-gray)', fontWeight: 400, marginLeft: '8px' }}>Usage ({filteredCount} / {totalRows})</span>
          </div>
          <button style={{
            background: 'var(--modal-bg-light-blue)',
            color: 'var(--modal-text-dark)',
            padding: '8px 12px',
            borderRadius: '8px',
            border: '0px'
          }} onClick={onClose}>Close</button>
        </div>
        <div className="pim-modal-surface__body pim-modal-stack">
          {readOnly && (
            <div className="pim-modal-alert pim-modal-alert--info">
              Viewer access: this modal is read-only.
            </div>
          )}
          <div className="pim-modal-layout" style={{ gridTemplateColumns: '360px 1fr' }}>
            <div className="pim-modal-stack">
              <div className="pim-modal-card" style={{ display: 'flex', gap: '8px' }}>
                <input placeholder="Search SKU Group…" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && doSearch()} style={{
                  width: '100%',
                  padding: '6px 8px',
                  border: '1px solid var(--modal-input-border-gray)',
                  borderRadius: '6px',
                  boxSizing: 'border-box'
                }} />
                <button style={{
                  background: 'var(--modal-bg-light-blue)',
                  color: 'var(--modal-text-dark)',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '0px'
                }} onClick={doSearch}>Search</button>
                <button style={{
                  background: 'var(--modal-bg-blue)',
                  color: 'var(--modal-text-white)',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '0px'
                }} onClick={() => {
                  if (readOnly) return;
                  setGroupForm({
                  SKUGroupKey: null,
                  SKUGroup: search,
                  SKUGroupDescription: ''
                });
                }} disabled={readOnly}>New Group</button>
              </div>
              <div className="pim-modal-card pim-modal-scroll" style={{ padding: '4px' }} onScroll={loadMoreIfNeeded} ref={resultsRef}>
                {(results || []).map(r => (
                  <div key={r.SKUGroupKey}
                       onClick={() => loadGroup(r.SKUGroupKey)}
                       style={{
                         cursor: 'pointer',
                         padding: '0.5rem',
                         background: selectedGroup?.SKUGroupKey === r.SKUGroupKey ? 'var(--highlight)' : 'transparent',
                         borderBottom: '1px solid var(--border)'
                       }}>
                    {r.SKUGroup} (Key #{r.SKUGroupKey})
                  </div>
                ))}
                {hasMore && (
                  <div style={{ textAlign: 'center', padding: '6px' }}>
                    <button onClick={() => doSearch(false)}>Load more</button>
                  </div>
                )}
                {!results.length && <div style={{ color: 'var(--modal-text-gray)', fontSize: '12px', padding: '6px' }}>No results</div>}
              </div>
            </div>
            <div className="pim-modal-rail" style={{ minHeight: 0 }}>
              <div className="pim-modal-card" style={{ display: 'grid', gap: '8px' }}>
                <label>
                  <div style={{ fontSize: '12px', color: 'var(--modal-text-gray)' }}>SKU Group</div>
                  <input style={{
                    width: '100%',
                    padding: '6px 8px',
                    border: '1px solid var(--modal-input-border-gray)',
                    borderRadius: '6px',
                    boxSizing: 'border-box'
                  }} value={groupForm.SKUGroup} onChange={e => onGroupField('SKUGroup', e.target.value)} readOnly={readOnly} />
                </label>
                <label>
                  <div style={{ fontSize: '12px', color: 'var(--modal-text-gray)' }}>SKU Group Description</div>
                  <input style={{
                    width: '100%',
                    padding: '6px 8px',
                    border: '1px solid var(--modal-input-border-gray)',
                    borderRadius: '6px',
                    boxSizing: 'border-box'
                  }} value={groupForm.SKUGroupDescription} onChange={e => onGroupField('SKUGroupDescription', e.target.value)} readOnly={readOnly} />
                </label>
              </div>
              <div className="pim-modal-card" style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '16px', marginTop: '12px', minHeight: 0, flex: 1 }}>
                <div className="pim-modal-card pim-modal-scroll" style={{ padding: '6px' }}>
                  <div style={{ fontWeight: 600, marginBottom: '6px' }}>SKUs in Group</div>
                  {(skus || []).map(sku => (
                    <div key={sku.SKUKey}
                         onClick={() => chooseSKU(sku)}
                         style={{
                           padding: '6px 8px',
                           border: '1px solid var(--modal-border-gray)',
                           borderRadius: '8px',
                           marginBottom: '6px',
                           cursor: 'pointer',
                           background: skuForm.SKUKey === sku.SKUKey ? 'var(--modal-bg-light-blue)' : 'var(--modal-bg-white-cell)'
                         }}>
                      <div style={{ fontWeight: 600 }}>{sku.SKU}</div>
                      <div style={{ fontSize: '12px', color: 'var(--modal-text-gray)' }}>{sku.Brand || ''}</div>
                    </div>
                  ))}
                  <button style={{
                    background: 'var(--modal-bg-light-blue)',
                    color: 'var(--modal-text-dark)',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '0px',
                    width: '100%',
                    marginTop: '6px'
                  }} onClick={() => {
                    if (readOnly) return;
                    setSkuForm({
                      SKUKey: null,
                      SKU: '',
                      SKUDescription: '',
                      Brand: '',
                      Quality: '',
                      LocationMade: '',
                      SKUGroupDifferences: ''
                    });
                    setApiData({});
                  }} disabled={readOnly}>
                    + New SKU
                  </button>
                </div>
                <div className="pim-modal-stack" style={{ minHeight: 0 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: '16px', minHeight: 0 }}>
                  <div style={{ gridColumn: '1' }}>
                    <label>
                      <div style={{ fontSize: '12px', color: 'var(--modal-text-gray)' }}>SKU</div>
                      <input
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          border: '1px solid var(--modal-input-border-gray)',
                          borderRadius: '6px',
                          boxSizing: 'border-box'
                        }}
                        value={skuForm.SKU}
                        onChange={e => onSkuField('SKU', e.target.value)}
                        readOnly={readOnly}
                      />
                    </label>
                    <label>
                      <div style={{ fontSize: '12px', color: 'var(--modal-text-gray)' }}>Quality</div>
                      <input
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          border: '1px solid var(--modal-input-border-gray)',
                          borderRadius: '6px',
                          boxSizing: 'border-box'
                        }}
                        value={skuForm.Quality}
                        onChange={e => onSkuField('Quality', e.target.value)}
                        readOnly={readOnly}
                      />
                    </label>
                    <label>
                      <div style={{ fontSize: '12px', color: 'var(--modal-text-gray)' }}>Barcode</div>
                      <input
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          border: '1px solid var(--modal-input-border-gray)',
                          borderRadius: '6px',
                          boxSizing: 'border-box'
                        }}
                        value={apiData.vad_ean_code || ''}
                        readOnly
                      />
                    </label>
                    <label>
                      <div style={{ fontSize: '12px', color: 'var(--modal-text-gray)' }}>UOM</div>
                      <input
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          border: '1px solid var(--modal-input-border-gray)',
                          borderRadius: '6px',
                          boxSizing: 'border-box'
                        }}
                        value={apiData.vad_uom || ''}
                        readOnly
                      />
                    </label>
                    <label>
                      <div style={{ fontSize: '12px', color: 'var(--modal-text-gray)' }}>Added to Orderwise</div>
                      <input
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          border: '1px solid var(--modal-input-border-gray)',
                          borderRadius: '6px',
                          boxSizing: 'border-box'
                        }}
                        value={apiData.vad_input_datetime ? new Date(apiData.vad_input_datetime).toLocaleString() : ''}
                        readOnly
                      />
                    </label>
                  </div>
                  <div style={{ gridColumn: '2' }}></div>
                  <div style={{ gridColumn: '3' }}>
                    <label>
                      <div style={{ fontSize: '12px', color: 'var(--modal-text-gray)' }}>Brand</div>
                      <input
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          border: '1px solid var(--modal-input-border-gray)',
                          borderRadius: '6px',
                          boxSizing: 'border-box'
                        }}
                        value={skuForm.Brand}
                        onChange={e => onSkuField('Brand', e.target.value)} // Editable for new SKUs
                        readOnly={readOnly}
                      />
                    </label>
                    <label>
                      <div style={{ fontSize: '12px', color: 'var(--modal-text-gray)' }}>Location Made</div>
                      <input
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          border: '1px solid var(--modal-input-border-gray)',
                          borderRadius: '6px',
                          boxSizing: 'border-box'
                        }}
                        value={skuForm.LocationMade}
                        onChange={e => onSkuField('LocationMade', e.target.value)} // Editable for new SKUs
                        readOnly={readOnly}
                      />
                    </label>
                    <label>
                      <div style={{ fontSize: '12px', color: 'var(--modal-text-gray)' }}>Weight</div>
                      <input
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          border: '1px solid var(--modal-input-border-gray)',
                          borderRadius: '6px',
                          boxSizing: 'border-box'
                        }}
                        value={apiData.vad_weight || ''}
                        readOnly
                      />
                    </label>
                    <label>
                      <div style={{ fontSize: '12px', color: 'var(--modal-text-gray)' }}>Last Modified in Orderwise</div>
                      <input
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          border: '1px solid var(--modal-input-border-gray)',
                          borderRadius: '6px',
                          boxSizing: 'border-box'
                        }}
                        value={apiData.vad_last_amended_datetime ? new Date(apiData.vad_last_amended_datetime).toLocaleString() : ''}
                        readOnly
                      />
                    </label>
                  </div>
                  <label style={{ gridColumn: 'span 3' }}>
                    <div style={{ fontSize: '12px', color: 'var(--modal-text-gray)' }}>SKU Description</div>
                    <textarea
                      style={{
                        width: '100%',
                        padding: '6px 8px',
                        border: '1px solid var(--modal-input-border-gray)',
                        borderRadius: '6px',
                        boxSizing: 'border-box',
                        height: '90px',
                        resize: 'vertical',
                        whiteSpace: 'normal'
                      }}
                      value={skuForm.SKUDescription}
                      onChange={e => onSkuField('SKUDescription', e.target.value)} // Editable for new SKUs
                      readOnly={readOnly}
                    />
                  </label>
                  <label style={{ gridColumn: 'span 3' }}>
                    <div style={{ fontSize: '12px', color: 'var(--modal-text-gray)' }}>SKU Group Differences</div>
                    <textarea
                      style={{
                        width: '100%',
                        padding: '6px 8px',
                        border: '1px solid var(--modal-input-border-gray)',
                        borderRadius: '6px',
                        boxSizing: 'border-box',
                        height: '30px',
                        resize: 'vertical'
                      }}
                      value={skuForm.SKUGroupDifferences}
                      onChange={e => onSkuField('SKUGroupDifferences', e.target.value)}
                      readOnly={readOnly}
                    />
                  </label>
                  </div>
                  <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <button
                      style={{
                        background: 'var(--modal-bg-blue)',
                        color: 'var(--modal-text-white)',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '0px'
                      }}
                      onClick={saveSKU}
                      disabled={busy || readOnly}
                    >
                      {skuForm.SKUKey ? 'Save SKU' : 'Create SKU'}
                    </button>
                    <button
                      style={{
                        background: 'var(--danger)',
                        color: 'var(--modal-text-white)',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '0px'
                      }}
                      onClick={deleteSKU}
                      disabled={!skuForm.SKUKey || busy || readOnly}
                    >
                      Remove from Group
                    </button>
                    {typeof onApply === 'function' && (
                      <button
                        style={{
                          background: 'var(--modal-bg-light-blue)',
                          color: 'var(--modal-text-dark)',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: '0px'
                        }}
                        onClick={handleUseInKit}
                        disabled={!groupForm.SKUGroupKey || readOnly}
                      >
                        Use in Kit
                      </button>
                    )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      <button
                        style={{
                          background: 'var(--modal-bg-blue)',
                          color: 'var(--modal-text-white)',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: '0px'
                        }}
                        onClick={saveGroup}
                        disabled={busy || readOnly}
                      >
                        {groupForm.SKUGroupKey ? 'Save Group' : 'Create Group'}
                      </button>
                      <button
                        style={{
                          background: 'var(--danger)',
                          color: 'var(--modal-text-white)',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: '0px'
                        }}
                        onClick={deleteGroup}
                        disabled={!groupForm.SKUGroupKey || busy || readOnly}
                      >
                        Delete Group
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="pim-modal-card">
            <div className="pim-modal-actions" style={{ justifyContent: 'space-between' }}>
              <div style={{ fontWeight: 600 }}>Usage</div>
              <div className="pim-modal-actions" style={{ marginLeft: 'auto' }}>
                <span style={{ color: 'var(--modal-text-gray)' }}>(<strong>{filteredCount}</strong> / {totalRows})</span>
                <button style={{
                  background: 'var(--modal-bg-light-blue)',
                  color: 'var(--modal-text-dark)',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '0px'
                }} onClick={openOemUsageWindow} disabled={!groupForm.SKUGroupKey}>OEM Usage</button>
                <button style={{
                  background: 'var(--modal-bg-light-blue)',
                  color: 'var(--modal-text-dark)',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '0px'
                }} onClick={clearFilters}>Clear Filters</button>
                <button style={{
                  background: 'var(--modal-bg-light-blue)',
                  color: 'var(--modal-text-dark)',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '0px'
                }} onClick={copyUsage} disabled={!filteredRows.length}>Copy</button>
              </div>
            </div>
            <div className="pim-modal-scroll" style={{
              maxHeight: '300px',
              border: '1px solid var(--modal-border-gray)',
              borderRadius: '8px'
            }} onScroll={onGridScroll} ref={gridRef}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0px' }}>
              <thead>
                <tr>
                  <th style={{
                    background: 'var(--modal-bg-gray)',
                    textAlign: 'left',
                    fontWeight: 600,
                    fontSize: '13px',
                    padding: '8px 10px',
                    borderBottom: '1px solid var(--modal-border-gray)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2
                  }}>Make</th>
                  <th style={{
                    background: 'var(--modal-bg-gray)',
                    textAlign: 'left',
                    fontWeight: 600,
                    fontSize: '13px',
                    padding: '8px 10px',
                    borderBottom: '1px solid var(--modal-border-gray)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2
                  }}>Model</th>
                  <th style={{
                    background: 'var(--modal-bg-gray)',
                    textAlign: 'left',
                    fontWeight: 600,
                    fontSize: '13px',
                    padding: '8px 10px',
                    borderBottom: '1px solid var(--modal-border-gray)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2
                  }}>Years</th>
                  <th style={{
                    background: 'var(--modal-bg-gray)',
                    textAlign: 'left',
                    fontWeight: 600,
                    fontSize: '13px',
                    padding: '8px 10px',
                    borderBottom: '1px solid var(--modal-border-gray)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2
                  }}>Region</th>
                  <th style={{
                    background: 'var(--modal-bg-gray)',
                    textAlign: 'left',
                    fontWeight: 600,
                    fontSize: '13px',
                    padding: '8px 10px',
                    borderBottom: '1px solid var(--modal-border-gray)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2
                  }}>ItemType</th>
                  <th style={{
                    background: 'var(--modal-bg-gray)',
                    textAlign: 'left',
                    fontWeight: 600,
                    fontSize: '13px',
                    padding: '8px 10px',
                    borderBottom: '1px solid var(--modal-border-gray)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2
                  }}>FitmentLocation</th>
                </tr>
                <tr>
                  <th style={{
                    background: 'var(--table-bg)',
                    padding: '6px 8px',
                    borderBottom: '1px solid var(--modal-border-gray)',
                    position: 'sticky',
                    top: '34px',
                    zIndex: 1
                  }}>
                    <input style={{
                      width: '100%',
                      padding: '4px 6px',
                      border: '1px solid var(--modal-border-gray)',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }} placeholder="e.g. Hon*" value={fMake} onChange={e => setFMake(e.target.value)} />
                  </th>
                  <th style={{
                    background: 'var(--table-bg)',
                    padding: '6px 8px',
                    borderBottom: '1px solid var(--modal-border-gray)',
                    position: 'sticky',
                    top: '34px',
                    zIndex: 1
                  }}>
                    <input style={{
                      width: '100%',
                      padding: '4px 6px',
                      border: '1px solid var(--modal-border-gray)',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }} placeholder="Model…" value={fModel} onChange={e => setFModel(e.target.value)} />
                  </th>
                  <th style={{
                    background: 'var(--table-bg)',
                    padding: '6px 8px',
                    borderBottom: '1px solid var(--modal-border-gray)',
                    position: 'sticky',
                    top: '34px',
                    zIndex: 1
                  }}>
                    <input style={{
                      width: '100%',
                      padding: '4px 6px',
                      border: '1px solid var(--modal-border-gray)',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }} placeholder="1999 or 2000-2003" value={fYears} onChange={e => setFYears(e.target.value)} />
                  </th>
                  <th style={{
                    background: 'var(--table-bg)',
                    padding: '6px 8px',
                    borderBottom: '1px solid var(--modal-border-gray)',
                    position: 'sticky',
                    top: '34px',
                    zIndex: 1
                  }}>
                    <input style={{
                      width: '100%',
                      padding: '4px 6px',
                      border: '1px solid var(--modal-border-gray)',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }} placeholder="Region…" value={fRegion} onChange={e => setFRegion(e.target.value)} />
                  </th>
                  <th style={{
                    background: 'var(--table-bg)',
                    padding: '6px 8px',
                    borderBottom: '1px solid var(--modal-border-gray)',
                    position: 'sticky',
                    top: '34px',
                    zIndex: 1
                  }}>
                    <input style={{
                      width: '100%',
                      padding: '4px 6px',
                      border: '1px solid var(--modal-border-gray)',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }} placeholder="ItemType…" value={fItemType} onChange={e => setFItemType(e.target.value)} />
                  </th>
                  <th style={{
                    background: 'var(--table-bg)',
                    padding: '6px 8px',
                    borderBottom: '1px solid var(--modal-border-gray)',
                    position: 'sticky',
                    top: '34px',
                    zIndex: 1
                  }}>
                    <input style={{
                      width: '100%',
                      padding: '4px 6px',
                      border: '1px solid var(--modal-border-gray)',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }} placeholder="Location…" value={fFitment} onChange={e => setFFitment(e.target.value)} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((r, i) => (
                  <tr key={`${r.Make}|${r.Model}|${r.Region}|${r.ItemType}|${r.FitmentLocation}|${i}`}>
                    <td style={{
                      padding: '8px 10px',
                      borderBottom: '1px solid var(--modal-table-border-gray)',
                      fontSize: '13px',
                      background: 'var(--modal-bg-white-cell)'
                    }}>{r.Make}</td>
                    <td style={{
                      padding: '8px 10px',
                      borderBottom: '1px solid var(--modal-table-border-gray)',
                      fontSize: '13px',
                      background: 'var(--modal-bg-white-cell)'
                    }}>{r.Model}</td>
                    <td style={{
                      padding: '8px 10px',
                      borderBottom: '1px solid var(--modal-table-border-gray)',
                      fontSize: '13px',
                      background: 'var(--modal-bg-white-cell)'
                    }}>{r.Years}</td>
                    <td style={{
                      padding: '8px 10px',
                      borderBottom: '1px solid var(--modal-table-border-gray)',
                      fontSize: '13px',
                      background: 'var(--modal-bg-white-cell)'
                    }}>{r.Region}</td>
                    <td style={{
                      padding: '8px 10px',
                      borderBottom: '1px solid var(--modal-table-border-gray)',
                      fontSize: '13px',
                      background: 'var(--modal-bg-white-cell)'
                    }}>{r.ItemType}</td>
                    <td style={{
                      padding: '8px 10px',
                      borderBottom: '1px solid var(--modal-table-border-gray)',
                      fontSize: '13px',
                      background: 'var(--modal-bg-white-cell)'
                    }}>{r.FitmentLocation}</td>
                  </tr>
                ))}
                {!visibleRows.length && (
                  <tr>
                    <td style={{
                      padding: '8px 10px',
                      borderBottom: '1px solid var(--modal-table-border-gray)',
                      fontSize: '13px',
                      background: 'var(--modal-bg-white-cell)',
                      color: 'var(--modal-text-gray)'
                    }} colSpan={6}>No usage</td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      </div>
      {oemUsageOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.45)',
          zIndex: 6000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            width: '92vw',
            maxWidth: '1500px',
            maxHeight: '90vh',
            background: 'var(--modal-bg-white)',
            color: 'var(--modal-text-black)',
            borderRadius: '12px',
            padding: '16px',
            display: 'grid',
            gridTemplateRows: 'auto 1fr',
            gap: '12px',
            boxShadow: 'var(--modal-shadow) 0px 20px 50px',
            position: 'relative'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '18px', fontWeight: 600 }}>OEM Usage</div>
                <div style={{ color: 'var(--modal-text-gray)', fontSize: '13px' }}>
                  {(selectedOem?.OEMNumber || 'Select an OEM')} • {selectedOem?.OEMType || 'Type N/A'} • {oemUsageStats.filtered} / {oemUsageStats.total} rows
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button style={{
                  background: 'var(--modal-bg-light-blue)',
                  color: 'var(--modal-text-dark)',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '0px'
                }} onClick={() => copyOemUsageRows(visibleOemUsageRows)} disabled={!visibleOemUsageRows.length}>Copy Rows</button>
                <button style={{
                  background: 'var(--modal-bg-light-blue)',
                  color: 'var(--modal-text-dark)',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '0px'
                }} onClick={clearOemUsageFilters}>Clear Filters</button>
                <button style={{
                  background: 'var(--modal-bg-blue)',
                  color: 'var(--modal-text-white)',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '0px'
                }} onClick={closeOemUsageModal}>Close</button>
              </div>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '320px 1fr',
              gap: '16px',
              minHeight: 0
            }}>
              <div style={{
                border: '1px solid var(--modal-border-gray)',
                borderRadius: '8px',
                padding: '12px',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0
              }}>
                <div style={{ fontWeight: 600, marginBottom: '8px' }}>OEMs ({(filteredOemList || []).length} / {(oemList || []).length})</div>
                <input
                  placeholder="Search OEM # or type"
                  value={oemSearch}
                  onChange={e => setOemSearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '6px 8px',
                    border: '1px solid var(--modal-border-gray)',
                    borderRadius: '6px',
                    marginBottom: '8px',
                    boxSizing: 'border-box'
                  }}
                />
                <div style={{
                  overflow: 'auto',
                  flex: 1,
                  border: '1px solid var(--modal-border-gray)',
                  borderRadius: '6px'
                }}>
                  {(filteredOemList || []).map(oem => (
                    <div
                      key={oem.OEMKey}
                      onClick={() => selectOemForModal(oem)}
                      style={{
                        padding: '8px',
                        borderBottom: '1px solid var(--modal-border-gray)',
                        cursor: 'pointer',
                        background: selectedOem?.OEMKey === oem.OEMKey ? 'var(--modal-bg-light-blue)' : 'var(--modal-bg-white-cell)'
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>{oem.OEMNumber || 'Unnamed OEM'}</div>
                      <div style={{ fontSize: '12px', color: 'var(--modal-text-gray)' }}>{oem.OEMType || 'Unknown Type'}</div>
                    </div>
                  ))}
                  {(!filteredOemList || !filteredOemList.length) && (
                    <div style={{ padding: '12px', color: 'var(--modal-text-gray)', fontSize: '12px' }}>No OEMs</div>
                  )}
                </div>
              </div>
              <div style={{
                border: '1px solid var(--modal-border-gray)',
                borderRadius: '8px',
                padding: '12px',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}>
                {oemUsageError && (
                  <div style={{
                    background: 'var(--danger)',
                    color: 'var(--modal-text-white)',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    marginBottom: '8px'
                  }}>{oemUsageError}</div>
                )}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                  gap: '8px',
                  marginBottom: '12px'
                }}>
                  <input value={oemFilters.make} onChange={e => handleOemFilterChange('make', e.target.value)} placeholder="Make filter" style={filterInputStyle} />
                  <input value={oemFilters.model} onChange={e => handleOemFilterChange('model', e.target.value)} placeholder="Model filter" style={filterInputStyle} />
                  <input value={oemFilters.years} onChange={e => handleOemFilterChange('years', e.target.value)} placeholder="Years filter" style={filterInputStyle} />
                  <input value={oemFilters.region} onChange={e => handleOemFilterChange('region', e.target.value)} placeholder="Region filter" style={filterInputStyle} />
                  <input value={oemFilters.itemType} onChange={e => handleOemFilterChange('itemType', e.target.value)} placeholder="ItemType filter" style={filterInputStyle} />
                  <input value={oemFilters.fitment} onChange={e => handleOemFilterChange('fitment', e.target.value)} placeholder="Fitment filter" style={filterInputStyle} />
                </div>
                <div style={{ flex: 1, overflow: 'auto', border: '1px solid var(--modal-border-gray)', borderRadius: '6px' }}>
                  <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                    <thead>
                      <tr>
                        {['Make', 'Model', 'Years', 'Region', 'ItemType', 'FitmentLocation'].map(header => (
                          <th key={header} style={oemTableHeaderStyle}>{header}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {visibleOemUsageRows.map((row, idx) => (
                        <tr key={`${row.Make}|${row.Model}|${row.Region}|${row.ItemType}|${row.FitmentLocation}|${idx}`}>
                          <td style={oemTableCellStyle}>{row.Make}</td>
                          <td style={oemTableCellStyle}>{row.Model}</td>
                          <td style={oemTableCellStyle}>{row.Years}</td>
                          <td style={oemTableCellStyle}>{row.Region}</td>
                          <td style={oemTableCellStyle}>{row.ItemType}</td>
                          <td style={oemTableCellStyle}>{row.FitmentLocation}</td>
                        </tr>
                      ))}
                      {!visibleOemUsageRows.length && (
                        <tr>
                          <td style={{ ...oemTableCellStyle, color: 'var(--modal-text-gray)' }} colSpan={6}>
                            {oemUsageBusy ? 'Loading usage…' : 'No usage rows'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            {oemUsageBusy && (
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(255,255,255,0.55)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '12px'
              }}>
                <div>Loading…</div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}